/**
 * The selected tab lives in the URL.
 *
 * Watching the leaderboard means refreshing it, and landing back on the
 * exchange every time makes that a two-step operation. The URL solves that and
 * three things client-side storage cannot: the back button works, a link to
 * /leaderboard opens the leaderboard for someone else, and what you see always
 * matches what the address bar says.
 *
 * The app is served at the root everywhere it is deployed -- production on its
 * own domain, PR previews on a per-branch subdomain -- so these paths are
 * absolute. CloudFront serves the app shell for unknown paths, which is what
 * makes a reload on /leaderboard work at all.
 */

export type View = "deposit" | "trade" | "news" | "leaderboard" | "about" | "rules" | "thanks";

export const VIEWS: readonly View[] = [
  "deposit",
  "trade",
  "news",
  "leaderboard",
  "about",
  "rules",
  "thanks",
];

/**
 * The view a path selects, or null when the path names no view -- including
 * "/", which means "decide for me" and defers to the first-load logic.
 *
 * Only a single segment counts. Real files are served from nested paths
 * (/docs/asyncapi.yaml), and treating the first segment of those as a view
 * would hijack them.
 */
export function viewFromPath(pathname: string): View | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length !== 1) return null;
  const segment = segments[0].toLowerCase();
  return VIEWS.find((v) => v === segment) ?? null;
}

/** The canonical path for a view. */
export function pathForView(view: View): string {
  return `/${view}`;
}
